import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import moment from 'moment'
import axios from '~/data/http/axios'
import axios2 from 'axios'
import Button from '~/Common-Objects/UI/Forms/Button/Button'
import DataTable1 from '~/Common-Objects/Tables/DataTable1/DataTable1'
import FormError from '~/Common-Objects/UI/Forms/FormError/FormError'
import Paper from '~/Common-Objects/UI/Paper/Paper'
import Select from '~/Common-Objects/UI/Forms/Select/Select'
import SnackBar from '~/Common-Objects/UI/Notifications/SnackBar/SnackBar'
import SquareChip from '~/Common-Objects/UI/Chips/SquareChip.jsx'
import TextInput from '~/Common-Objects/UI/Forms/TextInput/TextInput'
import WarningHolder from '~/Common-Objects/UI/Cards/WarningHolder'
import i18n from '~/i18n'
import { alterSiteDrawer } from '~/data/actions/siteActions'

const AUDIT_BETA = 'audit-events'

const RECORD_TYPES = [
    'campaign',
    'template',
    'segment',
    'automation',
    'automationContact',
    'campaignGroup',
    'campaignSegment',
    'socialPost'
]

const ACTION_CHIP = {
    create: 'square-chip-green',
    update: 'square-chip-blue',
    delete: 'square-chip-red',
    restore: 'square-chip-purple'
}

function isPlainObject(value) {
    return !!value && typeof value === 'object' && !Array.isArray(value)
}

function sameJson(left, right) {
    if (left === right) return true
    try {
        return JSON.stringify(left) === JSON.stringify(right)
    } catch (e) {
        return false
    }
}

function formatValue(value) {
    if (value === null || value === undefined) return null
    if (typeof value === 'boolean') return value ? i18n.t('company:audit.yes') : i18n.t('company:audit.no')
    if (typeof value === 'string') return value
    if (typeof value === 'number') return String(value)
    if (Array.isArray(value)) {
        if (!value.length) return null
        if (value.every((item) => isPlainObject(item) && (item.name || item.url))) {
            return value.map((item) => item.name || item.url).join('\n')
        }
    }
    try {
        return JSON.stringify(value, null, 2)
    } catch (e) {
        return String(value)
    }
}

/**
 * Split an object blob into the keys that differ, so a social template shows
 * the changed content instead of the whole templateJson.
 * @param {{ field: string, current: *, proposed: * }} entry
 * @returns {{ field: string, current: *, proposed: * }[]}
 */
function displayEntries(entry) {
    const rows = changedLeaves(entry.current, entry.proposed, entry.field)
    return rows && rows.length ? rows : [entry]
}

function changedLeaves(current, proposed, prefix) {
    const left = isPlainObject(current) ? current : null
    const right = isPlainObject(proposed) ? proposed : null
    if (!left && !right) return null
    const keys = new Set([...Object.keys(left || {}), ...Object.keys(right || {})])
    const rows = []
    keys.forEach((key) => {
        const before = left ? left[key] : undefined
        const after = right ? right[key] : undefined
        if (sameJson(before, after)) return
        const field = prefix ? prefix + '.' + key : key
        if (isPlainObject(before) || isPlainObject(after)) {
            const nested = changedLeaves(before, after, field)
            if (nested && nested.length) rows.push(...nested)
            else {
                rows.push({
                    field,
                    current: before === undefined ? null : before,
                    proposed: after === undefined ? null : after
                })
            }
        } else {
            rows.push({
                field,
                current: before === undefined ? null : before,
                proposed: after === undefined ? null : after
            })
        }
    })
    return rows
}

function fieldLabel(field) {
    const known = i18n.t('company:audit.fields.' + field, { defaultValue: '' })
    if (known) return known
    const last = String(field).split('.').pop()
    const knownLast = last !== field ? i18n.t('company:audit.fields.' + last, { defaultValue: '' }) : ''
    if (knownLast) return knownLast
    const words = String(last)
        .replace(/_/g, ' ')
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .trim()
    if (!words) return String(field)
    return words.charAt(0).toUpperCase() + words.slice(1)
}

function sourceLabel(source) {
    if (!source) return ''
    return i18n.t('company:audit.sources.' + source, { defaultValue: source })
}

function formatWhen(value) {
    if (!value) return ''
    const parsed = moment(value)
    return parsed.isValid() ? parsed.format('D MMM YYYY, HH:mm') : String(value)
}

function recordType(object) {
    return i18n.t('company:audit.types.' + object, { defaultValue: object || '—' })
}

function actionChip(action) {
    return (
        <SquareChip className={ACTION_CHIP[action] || 'square-chip-secondary'}>{action || '—'}</SquareChip>
    )
}

function AuditPreview({ event, onApplied }) {
    const [mode, setMode] = useState('undo')
    const [preview, setPreview] = useState(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState('')
    const [armed, setArmed] = useState(false)
    const [applying, setApplying] = useState(false)

    useEffect(() => {
        let cancelled = false
        setLoading(true)
        setError('')
        setArmed(false)
        setPreview(null)
        axios
            .post('/audit/preview', {
                eventId: event.id,
                index: event.index,
                mode
            })
            .then((response) => {
                if (!cancelled) setPreview(response.data)
            })
            .catch((err) => {
                if (!cancelled) {
                    setError(
                        (err.response && err.response.data && err.response.data.message) ||
                            i18n.t('company:audit.blocked')
                    )
                }
            })
            .then(() => {
                if (!cancelled) setLoading(false)
            })
        return () => {
            cancelled = true
        }
    }, [event.id, event.index, mode])

    const applyRestore = async () => {
        if (!preview || !preview.applicable || applying) return
        setApplying(true)
        setError('')
        try {
            await axios.post('/audit/apply', {
                eventId: event.id,
                index: event.index,
                mode,
                previewToken: preview.previewToken
            })
            onApplied()
        } catch (err) {
            setError(
                (err.response && err.response.data && err.response.data.message) || i18n.t('company:audit.blocked')
            )
            setArmed(false)
            setApplying(false)
        }
    }

    const title = event.recordName || recordType(event.object)
    const subtitle = event.recordName ? recordType(event.object) + ' · ' + event.objectId : String(event.objectId || '')
    const patch = (preview && preview.patch) || []

    return (
        <div className='audit-restore'>
            <h4 className='audit-restore__title'>{title}</h4>
            <p className='audit-restore__subtitle'>{subtitle}</p>
            <div className='audit-restore__meta'>
                {actionChip(event.action)}
                <span>{formatWhen(event.timestamp)}</span>
                {event.actorName ? <span>{event.actorName}</span> : null}
                {event.source ? <span>{sourceLabel(event.source)}</span> : null}
            </div>
            <Select
                outerClassName='mar-b25'
                headerText={i18n.t('company:audit.mode')}
                value={mode}
                data={[
                    { label: i18n.t('company:audit.modeUndo'), value: 'undo' },
                    { label: i18n.t('company:audit.modeAsOf'), value: 'asOf' }
                ]}
                onChange={(e) => setMode(e.target.value)}
            />
            {loading ? <p>{i18n.t('company:audit.loading')}</p> : null}
            {error ? <FormError className='mar-b25'>{error}</FormError> : null}
            {preview
                ? (preview.warnings || []).map((warning) => (
                      <WarningHolder key={warning} className='warning-holder-small'>
                          {warning}
                      </WarningHolder>
                  ))
                : null}
            {preview
                ? (preview.blockers || []).map((blocker) => (
                      <FormError key={blocker} className='mar-b10'>
                          {blocker}
                      </FormError>
                  ))
                : null}
            {preview && !patch.length && preview.applicable ? (
                <p className='mar-b25'>{i18n.t('company:audit.nothing')}</p>
            ) : null}
            {patch.flatMap(displayEntries).map((entry) => {
                const current = formatValue(entry.current)
                const proposed = formatValue(entry.proposed)
                return (
                    <div key={entry.field} className='audit-restore__field'>
                        <div className='audit-restore__field-name'>{fieldLabel(entry.field)}</div>
                        <div className='audit-restore__compare'>
                            <div>
                                <div className='audit-restore__caption'>{i18n.t('company:audit.current')}</div>
                                <div
                                    className={'audit-restore__value' + (current ? '' : ' audit-restore__value--empty')}
                                >
                                    {current || i18n.t('company:audit.emptyValue')}
                                </div>
                            </div>
                            <div>
                                <div className='audit-restore__caption'>{i18n.t('company:audit.proposed')}</div>
                                <div
                                    className={
                                        'audit-restore__value audit-restore__value--next' +
                                        (proposed ? '' : ' audit-restore__value--empty')
                                    }
                                >
                                    {proposed || i18n.t('company:audit.emptyValue')}
                                </div>
                            </div>
                        </div>
                    </div>
                )
            })}
            {preview && preview.applicable ? (
                <div className='audit-restore__actions'>
                    {armed ? (
                        <Button className='button-primary' disabled={applying} onClick={applyRestore}>
                            {applying ? i18n.t('company:audit.applying') : i18n.t('company:audit.confirm')}
                        </Button>
                    ) : (
                        <Button className='button-primary' disabled={applying} onClick={() => setArmed(true)}>
                            {i18n.t('company:audit.apply')}
                        </Button>
                    )}
                    {armed ? (
                        <button
                            type='button'
                            className='buttonSimple'
                            disabled={applying}
                            onClick={() => setArmed(false)}
                        >
                            {i18n.t('company:audit.cancel')}
                        </button>
                    ) : null}
                </div>
            ) : null}
        </div>
    )
}

export default function AuditEvents() {
    const dispatch = useDispatch()
    const user = useSelector((state) => state.user)
    const account = useSelector((state) => state.accountMaster)
    const betaOn = !!(
        account &&
        account.accountMaster &&
        account.accountMaster.beta &&
        account.accountMaster.beta[AUDIT_BETA]
    )
    const isAdmin = user?.userData?.roleId === 1

    const [object, setObject] = useState('')
    const [objectId, setObjectId] = useState('')
    const [dateRange, setDateRange] = useState(() => ({
        startDate: moment().subtract(90, 'days').startOf('day'),
        endDate: moment().endOf('day')
    }))
    const [events, setEvents] = useState([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')
    const [notice, setNotice] = useState('')
    const filtersRef = useRef({ object, objectId, dateRange })
    const requestId = useRef(0)
    const skipObjectIdLoad = useRef(true)
    const searchSource = useRef(null)
    filtersRef.current = { object, objectId, dateRange }

    const loadEvents = useCallback(async (options = {}) => {
        const id = ++requestId.current
        if (searchSource.current) searchSource.current.cancel('cancelled')
        const source = axios2.CancelToken.source()
        searchSource.current = source
        const filters = filtersRef.current
        setLoading(true)
        setError('')
        if (!options.keepNotice) setNotice('')
        try {
            const params = {}
            if (filters.object) params.object = filters.object
            if (filters.objectId) params.objectId = filters.objectId
            params.from = filters.dateRange.startDate.clone().startOf('day').toISOString()
            params.to = filters.dateRange.endDate.clone().endOf('day').toISOString()
            const response = await axios.get('/audit', { params, cancelToken: source.token })
            if (id !== requestId.current) return
            setEvents(response.data.events || [])
        } catch (err) {
            if (axios2.isCancel(err) || id !== requestId.current) return
            setEvents([])
            setError(
                (err.response && err.response.data && err.response.data.message) || i18n.t('company:audit.blocked')
            )
        }
        if (id === requestId.current) setLoading(false)
    }, [])

    useEffect(() => {
        return () => {
            if (searchSource.current) searchSource.current.cancel('cancelled')
        }
    }, [])

    useEffect(() => {
        if (!betaOn || !isAdmin) return
        loadEvents()
    }, [betaOn, isAdmin, object, dateRange, loadEvents])

    useEffect(() => {
        if (!betaOn || !isAdmin) return undefined
        if (skipObjectIdLoad.current) {
            skipObjectIdLoad.current = false
            return undefined
        }
        const timer = setTimeout(loadEvents, 400)
        return () => clearTimeout(timer)
    }, [objectId, betaOn, isAdmin, loadEvents])

    if (!betaOn || !isAdmin) return null

    const openPreview = (event) => {
        dispatch(
            alterSiteDrawer(
                true,
                true,
                'right',
                <AuditPreview
                    event={event}
                    onApplied={() => {
                        dispatch(alterSiteDrawer(false, true, 'right', undefined, true))
                        setNotice(i18n.t('company:audit.applied'))
                        loadEvents({ keepNotice: true })
                    }}
                />,
                true,
                720
            )
        )
    }

    const tableData = events.map((event) => ({
        when: {
            headerValue: i18n.t('company:audit.when'),
            value: formatWhen(event.timestamp),
            orderBy: false,
            width: 180
        },
        record: {
            headerValue: i18n.t('company:audit.record'),
            value: recordType(event.object),
            orderBy: false,
            width: 150
        },
        name: {
            headerValue: i18n.t('company:audit.name'),
            value: event.recordName ? (
                <span>
                    {event.recordName}
                    <div className='text-sml text-grey'>{event.objectId}</div>
                </span>
            ) : (
                event.objectId
            ),
            orderBy: false
        },
        by: {
            headerValue: i18n.t('company:audit.by'),
            value: event.actorName || '—',
            orderBy: false
        },
        action: {
            headerValue: i18n.t('company:audit.action'),
            value: actionChip(event.action),
            orderBy: false,
            width: 110
        },
        source: {
            headerValue: i18n.t('company:audit.source'),
            value: sourceLabel(event.source) || '—',
            orderBy: false,
            width: 140
        },
        review: {
            headerValue: '',
            value: (
                <button type='button' className='buttonSimple' onClick={() => openPreview(event)}>
                    {i18n.t('company:audit.review')}
                </button>
            ),
            orderBy: false,
            width: 110
        }
    }))

    return (
        <div>
            <p className='mar-b25'>{i18n.t('company:audit.intro')}</p>
            {error ? <FormError className='mar-b25'>{error}</FormError> : null}
            <div className='audit-filters mar-b25'>
                <div className='audit-filters__field'>
                    <Select
                        headerText={i18n.t('company:audit.object')}
                        value={object}
                        data={[
                            { label: i18n.t('company:audit.allTypes'), value: '' },
                            ...RECORD_TYPES.map((type) => ({ label: recordType(type), value: type }))
                        ]}
                        onChange={(e) => setObject(e.target.value)}
                    />
                </div>
                <div className='audit-filters__field'>
                    <TextInput
                        label={i18n.t('company:audit.objectId')}
                        value={objectId}
                        onChange={(e) => setObjectId(e.target.value)}
                    />
                </div>
                <div className='audit-filters__range'>
                    <TextInput
                        type='multiDate'
                        label={i18n.t('company:audit.dateRange')}
                        startDate={dateRange.startDate}
                        endDate={dateRange.endDate}
                        updateDate={(dates) => {
                            const startDate = moment(dates.startDate).startOf('day')
                            const endDate = moment(dates.endDate).endOf('day')
                            if (!startDate.isValid() || !endDate.isValid() || startDate.isAfter(endDate)) return
                            setDateRange((current) => {
                                if (
                                    startDate.isSame(current.startDate, 'day') &&
                                    endDate.isSame(current.endDate, 'day')
                                ) {
                                    return current
                                }
                                return { startDate, endDate }
                            })
                        }}
                    />
                </div>
            </div>
            <Paper>
                <DataTable1
                    noResultsTxt={<span>{i18n.t('company:audit.empty')}</span>}
                    tableData={tableData}
                    loadingData={loading ? 1 : 0}
                    width='100%'
                    hasCheckBoxes='no'
                />
            </Paper>
            <SnackBar message={notice} open={!!notice} autoclose={true} dismiss={() => setNotice('')} />
        </div>
    )
}
